import Ajv from 'ajv'
import addFormats from 'ajv-formats'
import {META_SCHEMA} from './meta-schema.js'

const ajv = new Ajv()
addFormats(ajv)

// Augmented schemas are memoised by their source object so ajv keeps caching the
// compiled validator by reference (and we never mutate the original schema).
const augmentedCache = new WeakMap()

// Adds the optional `_meta` provenance block as a sibling of `value` to every
// answer/value schema, while keeping the object strict (`additionalProperties:
// false`) so unrecognised siblings are still rejected.
//
// - A value-wrapper object (has a `value` property) gets `_meta` + the lock-down.
// - A wrapper object keyed by data-point id (e.g. `answerSchema`) is recursed one
//   level so its nested value-wrapper is covered while its own strictness is kept.
// - Anything else (ASSET `{ body }` schemas, range/constants schemas, arrays) is
//   returned untouched — ASSET answers have no `value`, so they stay out of scope.
const addMeta = schema => {
  if (!schema || typeof schema !== 'object' || Array.isArray(schema)) return schema

  if (schema.type === 'object' && schema.properties && 'value' in schema.properties) {
    if ('_meta' in schema.properties) return schema
    return {
      ...schema,
      additionalProperties: false,
      properties: {...schema.properties, _meta: META_SCHEMA},
    }
  }

  if (schema.type === 'object' && schema.properties) {
    let changed = false
    const properties = {}
    for (const [key, sub] of Object.entries(schema.properties)) {
      const next = addMeta(sub)
      properties[key] = next
      if (next !== sub) changed = true
    }
    return changed ? {...schema, properties} : schema
  }

  return schema
}

export const withMeta = schema => {
  if (!schema || typeof schema !== 'object') return schema
  if (augmentedCache.has(schema)) return augmentedCache.get(schema)
  const augmented = addMeta(schema)
  augmentedCache.set(schema, augmented)
  return augmented
}

export const validate = ({value, schema, customErrorMessage}) => {
  const compiled = ajv.compile(withMeta(schema))
  const valid = compiled(value)

  if (!valid) {
    const {errors} = compiled
    if (customErrorMessage) {
      throw new Error(customErrorMessage)
    }
    throw new Error(`[DataPointsLIB] Error: ${JSON.stringify(errors)}`)
  }
  return valid
}
